import { nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

import {
  reportRequestSchema,
  type ReportRequest,
  type ReportShareUserOption,
} from '../reportExplorer/model';

function parseIfJsonString(value: unknown) {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return value;
  }
}

const identifierSchema = z.union([z.string(), z.number()]).transform(String);
const serializedReportRequestSchema = z.preprocess(parseIfJsonString, reportRequestSchema);
const serializedChartReportsSchema = z.preprocess(
  parseIfJsonString,
  z.union([
    reportRequestSchema.transform((request) => [request]),
    z.array(serializedReportRequestSchema),
  ]),
);
const savedReportSettingsSchema = z.preprocess(
  parseIfJsonString,
  z.looseObject({
    filterValueLabels: z.record(z.string(), z.array(z.string())).nullish(),
  }),
);

export enum ReportDeleteTarget {
  Report = 'REPORT',
  Schedule = 'SCHEDULE',
}

export enum SavedReportSource {
  Owned = 'OWNED',
  Shared = 'SHARED',
}

export enum ReportDeliveryFrequency {
  Daily = 'DAILY',
  Weekly = 'WEEKLY',
  Monthly = 'MONTHLY',
}

export interface SavedReportUser {
  id: string;
  name: string | null;
  email: string | null;
  username: string | null;
}

const savedReportUserObjectSchema = z.looseObject({
  id: identifierSchema,
  name: nullableOptional(z.string()),
  email: nullableOptional(z.string()),
  username: nullableOptional(z.string()),
});

const savedReportUserSchema = z
  .union([identifierSchema, savedReportUserObjectSchema])
  .transform((user): SavedReportUser =>
    typeof user === 'string'
      ? { id: user, name: null, email: null, username: null }
      : {
          id: user.id,
          name: user.name ?? null,
          email: user.email ?? null,
          username: user.username ?? null,
        },
  );

const savedReportCreatorSchema = z
  .union([identifierSchema, savedReportUserObjectSchema])
  .nullish()
  .transform((creator): SavedReportUser | null => {
    if (!creator) return null;
    return typeof creator === 'string'
      ? { id: creator, name: null, email: null, username: null }
      : {
          id: creator.id,
          name: creator.name ?? null,
          email: creator.email ?? null,
          username: creator.username ?? null,
        };
  });

export const savedReportConfigSchema = z.looseObject({
  agencyId: z.string().nullish(),
  id: identifierSchema,
  name: z.string(),
  report: serializedReportRequestSchema,
  chartReport: serializedChartReportsSchema.nullish(),
  chartReports: serializedChartReportsSchema.nullish(),
  settings: savedReportSettingsSchema.nullish(),
  sharedWith: z.array(savedReportUserSchema).nullish(),
  createdBy: savedReportCreatorSchema,
  createdOn: nullableOptional(z.string()),
});

export const scheduledReportConfigSchema = z.looseObject({
  id: identifierSchema,
  recipients: z.array(z.string()).nullish(),
  deliveryFrequency: z.enum(ReportDeliveryFrequency),
  report: savedReportConfigSchema,
});

export type SavedReportConfigResponse = z.infer<typeof savedReportConfigSchema>;
export type ScheduledReportConfigResponse = z.infer<typeof scheduledReportConfigSchema>;

export interface SavedReportConfig {
  agencyId?: string | null;
  id: string;
  name: string;
  report: ReportRequest;
  chartReport: ReportRequest[];
  settings: SavedReportSettings;
  sharedWith: SavedReportUser[];
  createdBy: SavedReportUser | null;
  createdOn: string | null;
}

export interface SaveReportConfigPayload {
  name: string;
  report: ReportRequest;
  chartReport: ReportRequest[];
  settings: SavedReportSettings;
  sharedWith: string[];
}

export interface SavedReportSettings {
  filterValueLabels: Record<string, string[]>;
}

export interface ReportSchedulePayload {
  recipients: string[];
  deliveryFrequency: ReportDeliveryFrequency;
}

export interface ScheduledReportConfig extends ReportSchedulePayload {
  id: string;
  report: SavedReportConfig;
}

export function normalizeSavedReportConfig(
  response: SavedReportConfigResponse,
  fallback?: SaveReportConfigPayload,
): SavedReportConfig {
  return {
    id: response.id,
    agencyId: response.agencyId ?? null,
    name: response.name,
    report: response.report,
    chartReport: response.chartReports ?? response.chartReport ?? fallback?.chartReport ?? [],
    settings: {
      filterValueLabels:
        response.settings?.filterValueLabels ?? fallback?.settings.filterValueLabels ?? {},
    },
    sharedWith:
      response.sharedWith ??
      fallback?.sharedWith.map((id) => ({
        id,
        name: null,
        email: null,
        username: null,
      })) ??
      [],
    createdBy: response.createdBy,
    createdOn: response.createdOn ?? null,
  };
}

export function normalizeScheduledReportConfig(
  response: ScheduledReportConfigResponse,
): ScheduledReportConfig {
  return {
    id: response.id,
    recipients: response.recipients ?? [],
    deliveryFrequency: response.deliveryFrequency,
    report: normalizeSavedReportConfig(response.report),
  };
}

export function savedReportSharedUserIds(report: SavedReportConfig) {
  return report.sharedWith.map(({ id }) => id);
}

export function savedReportUserLabel(user: SavedReportUser) {
  return user.name ?? user.email ?? user.username ?? user.id;
}

export function toReportShareUserOption(user: SavedReportUser): ReportShareUserOption {
  const email = user.email ?? user.username ?? user.id;
  return {
    id: user.id,
    email,
    label: user.name ? `${user.name} - ${email}` : email,
  };
}
